'use client';

import { Container, GridBg, MotionBlock, PrimaryButton, SecondaryButton } from './shared';

type MarketingHeroProps = {
  eyebrow: string;
  title: string;
  subtitle: string;
  primaryCtaLabel: string;
  primaryCtaHref: string;
  secondaryCtaLabel?: string;
  secondaryCtaHref?: string;
  highlights?: string[];
  variant?: 'home' | 'inner';
};

function splitTitleForAccent(title: string) {
  const normalized = title.trim();
  const firstPeriod = normalized.indexOf('. ');
  if (firstPeriod === -1) {
    return { main: normalized, accent: '' };
  }
  const main = normalized.slice(0, firstPeriod + 1).trim();
  const accent = normalized.slice(firstPeriod + 1).trim();
  if (!accent) {
    return { main: normalized, accent: '' };
  }
  return { main, accent };
}

export function MarketingHero({
  eyebrow,
  title,
  subtitle,
  primaryCtaLabel,
  primaryCtaHref,
  secondaryCtaLabel,
  secondaryCtaHref,
  highlights,
  variant = 'inner',
}: MarketingHeroProps) {
  const hasHighlights = Boolean(highlights && highlights.length > 0);
  const hasSecondary = Boolean(secondaryCtaLabel && secondaryCtaHref);
  const titleParts = splitTitleForAccent(title);
  const titleClass =
    variant === 'home'
      ? 'text-[clamp(2.25rem,5vw,3.75rem)]'
      : 'text-[clamp(2rem,4vw,3.15rem)]';
  const sectionClass =
    variant === 'home'
      ? 'relative overflow-hidden border-b border-white/[0.08] pb-20 pt-8 md:pb-32 md:pt-12'
      : 'relative overflow-hidden border-b border-white/[0.08] pb-16 pt-8 md:pb-24 md:pt-12';

  return (
    <section className={sectionClass}>
      <GridBg />
      <Container className="relative">
        <div className={variant === 'home' ? 'grid items-center gap-12 lg:grid-cols-2 lg:gap-16' : 'max-w-4xl'}>
          <MotionBlock>
            <p className="mb-4 inline-flex rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 py-1 text-xs text-cadrino-muted">
              {eyebrow}
            </p>
            <h1
              className={`${titleClass} font-semibold leading-[1.08] tracking-[-0.02em] text-slate-50`}
            >
              {titleParts.main}
              {titleParts.accent && <span className="block text-cadrino-primary">{titleParts.accent}</span>}
            </h1>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-cadrino-muted md:text-lg">
              {subtitle}
            </p>
            {hasHighlights && (
              <div className="mt-8 flex flex-wrap gap-3">
                {highlights?.map((item) => (
                  <span
                    key={item}
                    className="rounded-full border border-white/[0.08] bg-white/[0.04] px-3 py-1.5 text-xs text-slate-300"
                  >
                    {item}
                  </span>
                ))}
              </div>
            )}
            <div className="mt-8 flex flex-wrap gap-3">
              <PrimaryButton href={primaryCtaHref}>{primaryCtaLabel}</PrimaryButton>
              {hasSecondary && (
                <SecondaryButton href={secondaryCtaHref as string}>
                  {secondaryCtaLabel as string}
                </SecondaryButton>
              )}
            </div>
          </MotionBlock>
        </div>
      </Container>
    </section>
  );
}
